import { RequestRoot, uCRC } from "types";
import { IPrintDepartment } from "../model/print-department";
import { PrintQRCodeGenerationService } from "../service/print-qrcode-generation.service";
import { JobRecord } from "../../features";
import { RequestFeaturesV2 } from "../../features2";

export class PrintFormV2 {
    model: RequestRoot;
    department: IPrintDepartment;
    version: string;

    constructor(private qrCodeService: PrintQRCodeGenerationService) { }

    calculateModelCrc32(): string {
        return uCRC(JSON.stringify(this.model)).toString(36).toUpperCase();
    }

    get model$() {
        return this.model.features as RequestFeaturesV2;
    }

    get photo$() {
        return this.model.readBlobURL('applicant.printPhoto');
    }

    get applicant$() {
        return this.model$.applicant;
    }

    get representative$() {
        return this.model$.representative;
    }

    get hasAnotherRepresentative$() {
        return this.representative$.trustRecipient.active;
    }

    private convertJob(r: JobRecord): any {
        return {
            from: this.toLocaleJobString(r.from),
            to: this.toLocaleJobString(r.to),
            organization: `${r.position}`,
            address: r.address
        };
    }

    get jobHistoryHead$(): any[] {
        const head = this.applicant$.jobRecords.slice(0, 8);
        const result = head.map(r => this.convertJob(r));
        result.length = 8;
        return result;
    }

    get jobHistoryPages$() {
        return Math.ceil((this.applicant$.jobRecords.length - 8) / 11);
    }

    getJobValue$(page: number, row: number) {
        const index = page * 11 + row + 8;
        if (index < this.applicant$.jobRecords.length) {
            return this.convertJob(this.applicant$.jobRecords[index]);
        }
        return null;
    }

    get hasForeignPassport$() {
        return !!this.applicant$.activeFPassports.p1;
    }

    get firstFPassportFilled$(): any {
        if (this.applicant$.activeFPassports.p1) {
            return {
                series: this.applicant$.activeFPassports.p1.series,
                number: this.applicant$.activeFPassports.p1.number,
                issuer: this.applicant$.activeFPassports.p1.issuer,
                issueDate: this.toLocaleDateString(this.applicant$.activeFPassports.p1.issueDate),
                mustBeCanceled: this.applicant$.activeFPassports.p1.mustBeCanceled,
                lost: this.applicant$.activeFPassports.p1.lost
            };
        }
        return { series: "", number: "", issuer: "", issueDate: "", mustBeCanceled: null, lost: false };
    }

    get secondFPassportFilled$(): any {
        if (this.applicant$.activeFPassports.p2) {
            return {
                series: this.applicant$.activeFPassports.p2.series,
                number: this.applicant$.activeFPassports.p2.number,
                issuer: this.applicant$.activeFPassports.p2.issuer,
                issueDate: this.toLocaleDateString(this.applicant$.activeFPassports.p2.issueDate),
                mustBeCanceled: this.applicant$.activeFPassports.p2.mustBeCanceled,
                lost: this.applicant$.activeFPassports.p2.lost
            };
        }
        return { series: "", number: "", issuer: "", issueDate: "", mustBeCanceled: null, lost: false };
    }

    get nameChangePages$() {
        return Math.ceil((this.applicant$.nameChanges.length - 1) / 7);
    }

    getNameChangeValue$(page: number, row: number) {
        const index = page * 7 + row + 1;
        if (index < this.applicant$.nameChanges.length) {
            return this.applicant$.nameChanges[index];
        }
        return null;
    }

    get recentLastName$() {
        return this.applicant$.nameChanges.length ? this.applicant$.nameChanges[0].lastName : "";
    }

    get recentFirstName$() {
        return this.applicant$.nameChanges.length ? this.applicant$.nameChanges[0].firstName : "";
    }

    get recentMiddleName$() {
        return this.applicant$.nameChanges.length ? this.applicant$.nameChanges[0].middleName.value : "";
    }

    get nameChangeYear$() {
        return this.applicant$.nameChanges.length ? this.toLocaleDateString(this.applicant$.nameChanges[0].year) : "";
    }

    get nameChangeLocation$() {
        return this.applicant$.nameChanges.length ? this.applicant$.nameChanges[0].location : "";
    }

    get isSecretAccess$() {
        return this.model$?.secretAccess?.value?.val() === '1';
    }

    get isContractObligations$() {
        return this.model$?.contractObligations?.value?.val() === '1';
    }

    get isMilitaryService$() {
        return this.model$?.militaryService?.val() === '1';
    }

    get isConvicted$() {
        return this.model$?.convicted?.val() === '1';
    }

    get isAccused$() {
        return this.model$?.accused?.val() === '1';
    }

    get isLegalObligations$() {
        return this.model$?.legalObligations?.val() === '1';
    }

    get isBankrupt$() {
        return this.model$?.bankrupt?.val() === '1';
    }

    get isRestricted$() {
        return this.isSecretAccess$ || this.isContractObligations$ || this.isMilitaryService$ || this.isConvicted$ || this.isAccused$ || this.isLegalObligations$ || this.isBankrupt$;
    }

    get restrictedOrganization$() {
        if (this.isSecretAccess$ && !this.isContractObligations$) {
            return this.model$.secretAccess.organization;
        }
        if (!this.isSecretAccess$ && this.isContractObligations$) {
            return this.model$.contractObligations.organization;
        }
        if (this.isSecretAccess$ && this.isContractObligations$) {
            return this.model$.secretAccess.organization + ', ' + this.toLocaleYearString(this.model$.secretAccess.year) + '; ' + this.model$.contractObligations.organization;
        }
        return '';
    }

    get restrictedYear$() {
        if (this.isSecretAccess$ && !this.isContractObligations$) {
            return this.toLocaleYearString(this.model$.secretAccess.year);
        }
        if (!this.isSecretAccess$ && this.isContractObligations$) {
            return this.toLocaleYearString(this.model$.contractObligations.year);
        }
        if (this.isSecretAccess$ && this.isContractObligations$) {
            return this.toLocaleYearString(this.model$.contractObligations.year);
        }
        return '';
    }

    private qrImg: string | null = null;
    private qrImgInet: string | null = null;
    private qrErr: boolean | null = null;
    private qrInit() {
        if (this.qrErr === null && this.model && this.department) {
            const qrImg = this.qrCodeService.getEmbedQrCode(this.model, this.department);
            const qrImgInet = this.qrCodeService.getInetQrCode(this.model);
            if (typeof qrImg === 'string' && typeof qrImgInet === 'string') {
                this.qrImg = qrImg;
                this.qrImgInet = qrImgInet;
                this.qrErr = false;
            } else {
                this.qrImg = '';
                this.qrImgInet = '';
                this.qrErr = true;
            }
        }
    }

    get qrSucc() {
        this.qrInit();
        return this.qrErr === false;
    }

    get qrFail() {
        this.qrInit();
        return this.qrErr === true;
    }

    get qrCodeImage() {
        return this.qrImg;
    }

    get qrCodeImageInet() {
        return this.qrImgInet;
    }

    get qrCodeImageNormalW() {
        return this.qrCodeService.getEmbedImageWidthNormal();
    }

    get qrCodeImageNormalH() {
        return this.qrCodeService.getEmbedImageHeightNormal();
    }

    get qrCodeImageLargeW() {
        return this.qrCodeService.getEmbedImageWidthLarge();
    }

    get qrCodeImageLargeH() {
        return this.qrCodeService.getEmbedImageHeightLarge();
    }

    get qrCodeImageHugeW() {
        return this.qrCodeService.getEmbedImageWidthHuge();
    }

    get qrCodeImageHugeH() {
        return this.qrCodeService.getEmbedImageHeightHuge();
    }

    get inetQrCodeImageW() {
        return this.qrCodeService.getInetImageWidth();
    }

    get inetQrCodeImageH() {
        return this.qrCodeService.getInetImageHeight();
    }

    toLocaleDateString(x: { value: string } | undefined) {
        if (!x || !x.value) {
            return '';
        }
        return x.value.substr(8, 2).replace('00', 'XX') + '.' + x.value.substr(5, 2).replace('00', 'XX') + '.' + x.value.substr(0, 4);
    }

    toLocaleJobString(x: { value: string | null }) {
        if (!x || !x.value) {
            return 'Н.ВР.';
        }
        return x.value.substr(5, 2) + '.' + x.value.substr(0, 4);
    }

    toLocaleYearString(x: { value: string } | undefined) {
        if (!x || !x.value) {
            return '';
        }
        return x.value.substr(0, 4);
    }
}
